import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import type { AuthTokens, AuthUser } from '@/types/auth';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // epoch ms
  sessionId: string | null;
  user: AuthUser | null;
  setSession: (args: {
    tokens: AuthTokens;
    user: AuthUser;
    sessionId?: string | null;
  }) => void;
  setTokens: (tokens: AuthTokens) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

function computeExpiry(token: string, expiresIn?: number): number | null {
  if (expiresIn && Number.isFinite(expiresIn)) {
    return Date.now() + expiresIn * 1000;
  }
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

// localStorage keys mirror the reference apps so a manual migration is trivial.
const STORAGE_KEY = 'ticketbox_auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      sessionId: null,
      user: null,

      setSession: ({ tokens, user, sessionId }) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: computeExpiry(tokens.accessToken, tokens.expiresIn),
          sessionId: sessionId ?? null,
          user,
        }),

      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: computeExpiry(tokens.accessToken, tokens.expiresIn),
        }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          sessionId: null,
          user: null,
        }),

      isAuthenticated: () => {
        const { accessToken, refreshToken } = get();
        return Boolean(accessToken && refreshToken);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        expiresAt: s.expiresAt,
        sessionId: s.sessionId,
        user: s.user,
      }),
    },
  ),
);

/** Non-React accessor for interceptors. */
export const authStore = {
  get: () => useAuthStore.getState(),
};
